/**
 * Altyazı **ölçekleme** — video yüksekliğine göre.
 *
 * ## Ölçek neden bu kadar zor?
 *
 * Altyazının okunabilirliği videonun piksel yüksekliğine bağlıdır. Doğru CSS
 * birimi `cqh`, ama o `container-type: size` ister; o da kutuya `contain: size`
 * uygulayıp genişliğini 0 yapardı. `.watch__stage` tam ekran hedefidir ve
 * `width: auto` ile **video'yu sarar** — ölçümü CSS'e bırakmak dünkü tam ekran
 * çözümünü bozardı.
 *
 * Bu yüzden ölçüm **JS ile** yapılır: `ResizeObserver` kutu boyutunu izler ve
 * `--watch-vh` yazar.
 *
 * ## Gerçek sunucuda bulunan hata (iki tane)
 *
 * 1. **Ölçüm hiç yapılmıyordu.** `useEffect` deps'i `[stageRef]` idi — bir ref
 *    nesnesi, kimse değiştirmez. Oynatma hazır değilken bileşen `Preparing`
 *    ekranını döndürür, `.watch__stage` DOM'da **yoktur**, etiket `null`
 *    döner ve bir daha çalışmaz. Film başlayınca kutu oluştu ama gözlemci
 *    bağlanmadı; ölçüm `null` kaldı.
 *
 * 2. **Yedek değer `45vh` temel varsayımı yanlıştı.** "Video yüksekliği ≈ ekran
 *    yüksekliği" denmişti; oysa pencere modunda video ekranın ~%35'idir.
 *    720 px ekranda `45vh` = 324 px → altyazı resmin dışına taşıp ~420 px
 *    boyutunda göründü ("altyazı kocaman").
 *
 * Düzeltmeler: (1) **geri çağırma referansı** (callback ref) + ölçümü
 * `useState` üzerinden tetikleme, ayrıca ilk ölçüm `getBoundingClientRect`
 * ile **eşzamanlı** alınır (ResizeObserver bir sonraki karede tetiklenir);
 * (2) yedek artık **video oranına** dayanır, `45vh` değil.
 */

import type { CSSProperties } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { type ElementRef, useElementRef } from './useElementRef.ts';
import type { VideoFit } from './videoFit.ts';

/**
 * Oynatıcı yüksekliğinin yüzde kaçı altyazı punto olsun.
 *
 * 5 → 1080p'de ~54 px, 370 px'lik gömülü oynatıcıda ~18,5 px. Plex/Netflix
 * ölçeği (5%); hem tam ekranda hem pencere modunda okunur.
 */
export const BASE_RATIO = 0.05;

/** Tercih çarpanları (küçük / normal / büyük). */
export const SIZE_SCALES = { small: 0.8, medium: 1, large: 1.3 } as const;

/** Boyut düğmelerinin görünen adları. */
export const SIZE_LABELS = { small: 'Küçük', medium: 'Normal', large: 'Büyük' } as const;

export type SubtitleSize = keyof typeof SIZE_SCALES;

/**
 * Ölçüm yapılamazsa kullanılan yedek punto tabanı.
 *
 * `vw` tabanlıdır, çünkü video **genişliği** ekranla sınırlıdır (`max-width`
 * 1000 px) ama `vh` tabanlı **değildir**: pencere modunda oynatıcı ekranın
 * yarısını geçmez. Önceki `45vh` yedeği 720 px ekranda 324 px'e çıkıyordu.
 */
const FALLBACK_BASE = 'clamp(0.9rem, 2.2vw, 1.6rem)';

export interface StageMetrics extends ElementRef<HTMLElement> {
  /** Kutu yüksekliği; ölçülmediyse `null`. */
  heightPx: number | null;
  /** `.watch__stage` için satır içi stil (`--watch-vh`). */
  style: CSSProperties;
}

export function useStageMetrics(): StageMetrics {
  // Geç bağlanan öğe: kutu ancak oynatma hazır olduğunda DOM'a giriyor.
  // `useEffect([stageRef])` deseni burada da boş kalırdı (bkz. `useElementRef`).
  const elementRef = useElementRef<HTMLElement>();
  const [heightPx, setHeightPx] = useState<number | null>(null);

  // İlk ölçüm **eşzamanlı**: `ResizeObserver` bir sonraki karede bildirir ve bu
  // arada yanlış yedek (`clamp(...)`) birkaç kare görünür.
  const attach = useCallback(
    (node: HTMLElement | null) => {
      elementRef.attach(node);
      if (node === null) {
        setHeightPx(null);
        return;
      }
      const measured = node.getBoundingClientRect().height;
      setHeightPx(measured > 0 ? measured : null);
    },
    [elementRef],
  );

  useEffect(() => {
    if (elementRef.element === null) return;
    if (typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver((entries) => {
      const height = entries[0]?.contentRect.height;
      // 0, gizli/yükleniyor durumunda (yer tutucu) ölçümü demektir.
      if (height !== undefined && height > 0) setHeightPx(height);
    });
    observer.observe(elementRef.element);
    return () => observer.disconnect();
  }, [elementRef.element]);

  return {
    attach,
    ref: elementRef.ref,
    element: elementRef.element,
    heightPx,
    // `px` yerine sayı: CSS'te çarpım için birimsiz sayı gerekir.
    style: heightPx === null ? {} : ({ '--watch-vh': heightPx } as CSSProperties),
  };
}

/**
 * **Resmin gerçek sınırlarını** ölçer — siyah bant dahil.
 *
 * `.watch__video` üzerinde `aspect-ratio: 16 / 9` sabit olduğu için öğenin
 * kutusu her zaman 16:9'dur; sinema oranlı (2.35:1) bir kaynak `object-fit:
 * contain` ile **kutunun içinde** küçülür ve altta/üstte siyah bant oluşur.
 * Altyazının bandın içine inebilmesi için o bant bilinmelidir.
 *
 * Hesap `contain` kurallarını birebir uygular:
 *
 * ```text
 *   ölçek = min(kutuGenişlik / videoGenişlik, kutuYükseklik / videoYükseklik)
 *   görünen Y = videoYükseklik × ölçek
 *   üst boşluk = (kutuYükseklik − görünen Y) / 2
 * ```
 *
 * `videoWidth`/`videoHeight` yalnızca `loadedmetadata` sonrası bilinir; bu
 * yüzden hem o olay dinlenir hem de boyut değişiminde yeniden ölçülür.
 */
export function usePictureBounds(
  stageElement: HTMLElement | null,
  videoElement: HTMLVideoElement | null,
  fit: VideoFit = 'contain',
): PictureBounds {
  const [bounds, setBounds] = useState<PictureBounds>(EMPTY_PICTURE_BOUNDS);

  const measure = useCallback((): void => {
    if (!stageElement || !videoElement) {
      setBounds(EMPTY_PICTURE_BOUNDS);
      return;
    }
    // Meta veri yokken en-boy oranı bilinmiyor; `contain` hesabı yapılamaz.
    if (!videoElement.videoWidth || !videoElement.videoHeight) {
      setBounds(EMPTY_PICTURE_BOUNDS);
      return;
    }

    const stage = stageElement.getBoundingClientRect();
    const box = videoElement.getBoundingClientRect();
    if (stage.height <= 0 || box.width <= 0 || box.height <= 0) {
      setBounds(EMPTY_PICTURE_BOUNDS);
      return;
    }

    // `cover` görüntüyü kutunun tamamına yayar: siyah bant yoktur, kenarlar
    // kırpılır. Altyazı sınırı da kutunun tamamıdır.
    if (fit === 'cover') {
      const topInStage = box.top - stage.top;
      setBounds({
        heightPx: box.height,
        bottomPx: stage.height - (topInStage + box.height),
        topPx: stage.height - topInStage,
      });
      return;
    }

    const scale = Math.min(
      box.width / videoElement.videoWidth,
      box.height / videoElement.videoHeight,
    );
    const visibleHeight = videoElement.videoHeight * scale;
    // `contain` görüntüyü **ortalar**: boşluk üst ve altta eşit dağılır.
    const topInStage = box.top - stage.top + (box.height - visibleHeight) / 2;

    setBounds({
      heightPx: visibleHeight,
      /*
       * Hem `bottomPx` hem `topPx` **kutu tabanından** ölçülür: altyazının
       * konumu bir `bottom` değeri olarak hesaplandığı için tek referans
       * (kutu tabanı) yeterlidir. Üst kenar "yukarıdan" ölçülürse
       * `computeSubtitleBottomPx`'in tavanı yanlış yere düşer.
       */
      bottomPx: stage.height - (topInStage + visibleHeight),
      topPx: stage.height - topInStage,
    });
  }, [stageElement, videoElement, fit]);

  useEffect(() => {
    measure();
  }, [measure]);

  useEffect(() => {
    if (!stageElement || !videoElement) return;

    // Kutu yeniden boyutlanınca (pencere, tam ekran, yön değişimi).
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => measure());
    observer.observe(stageElement);
    observer.observe(videoElement);

    // Meta veri `loadedmetadata` ile gelir; ayrıca boyut değişebilir.
    const onMeta = (): void => measure();
    videoElement.addEventListener('loadedmetadata', onMeta);
    videoElement.addEventListener('resize', onMeta);
    window.addEventListener('resize', onMeta);

    return () => {
      observer.disconnect();
      videoElement.removeEventListener('loadedmetadata', onMeta);
      videoElement.removeEventListener('resize', onMeta);
      window.removeEventListener('resize', onMeta);
    };
  }, [stageElement, videoElement, measure]);

  return bounds;
}

/** Kontrol çubuğu yüksekliği + güvenlik payı (px); altyazının alt sınırı. */
export const SUBTITLE_CONTROLS_GAP_PX = 12;

/**
 * Kontroller **gizliyken** altyazının en alt konumu (px).
 *
 * ## Neden gerekli?
 *
 * Kontrol çubuğu ~120 px yüksekliğinde; sinema oranlı bir filmdeki alt
 * siyah bant ise çoğu zaman **daha kısadır** (~100 px). Yani kontrol
 * çubuğu bantın tamamını kapatır ve "siyah banta indir" ayarı hiçbir işe
 * yaramaz — altyazı zemine çakılır.
 *
 * Oynatma sırasında kontrol çubuğu 3,5 sn hareketsizlikte kendiliğinden
 * gizlenir; o anda altyazının bant içine yerleşmesi engellenmemelidir. Plex
 * de kontrol çubuğu belirince altyazıyı yukarı iter, gizlenince geri
 * getirir. Aynı davranış.
 */
export const SUBTITLE_MIN_BOTTOM_PX = 16;

/**
 * Kontrol çubuğunun kapladığı yüksekliği ölçer.
 *
 * `PlayerBar` yüksekliği içeriğe göre değişir (uyarı satırı, çentikli
 * ilerleme rayı, hız seçici satırı); sabit bir sayıya güvenmek "altyazı
 * kontrol düğmelerinin altında kaldı" hatalarını geri getirirdi.
 *
 * Ölçüm bilinçli olarak DOM'dan yapılır: `PlayerBar`'a yükseklik bildiren
 * bir geri çağırma eklemek yerine tek bir okuma, iki bileşen arasındaki
 * bağımlılığı da kaldırıyor.
 */
export function useControlsSafePx(stageElement: HTMLElement | null): number | null {
  const [px, setPx] = useState<number | null>(null);

  useEffect(() => {
    if (!stageElement) {
      setPx(null);
      return;
    }

    const read = (): void => {
      const bar = stageElement.querySelector('.player');
      const height = bar instanceof HTMLElement ? bar.getBoundingClientRect().height : 0;
      setPx(height > 0 ? Math.ceil(height + SUBTITLE_CONTROLS_GAP_PX) : null);
    };

    read();

    const bar = stageElement.querySelector('.player');
    if (!bar || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(read);
    observer.observe(bar);
    return () => observer.disconnect();
  }, [stageElement]);

  return px;
}

/**
 * Altyazı punto CSS değişkenleri (ölçüm + kullanıcı tercihi).
 *
 * @param stageHeightPx Ölçüm; `null` → video oranına dayalı yedek
 */
export function subtitleVars(
  stageHeightPx: number | null,
  size: SubtitleSize,
  bold: boolean,
): CSSProperties {
  const scale = SIZE_SCALES[size];
  // Ölçüm varsa piksel hesabı yapılır; yoksa **orantılı yedek** kullanılır.
  // İkisi de geçerli bir `font-size` uzunluğu döner (birimli).
  const fontSize =
    stageHeightPx !== null && stageHeightPx > 0
      ? `${stageHeightPx * BASE_RATIO * scale}px`
      : `calc(${FALLBACK_BASE} * ${scale})`;

  return {
    '--sub-scale': fontSize,
    // Değişken değerleri metindir; `font-weight` birimsizdir ama CSS değişkeni
    // daima metin taşır.
    '--sub-weight': bold ? '700' : '600',
  } as CSSProperties;
}

/**
 * Altyazının **dikey konumu** — resmin kendisine göre, kutuya göre değil.
 *
 * ## Sorun
 *
 * Konum sabit bir yüzdeyle (`bottom: max(12%, 92px)`) **oynatıcı kutusuna**
 * göre hesaplanıyordu. Oysa sinema oranlı (2.35:1, 2.40:1) bir film 16:9
 * kutuda oynatıldığında altta ve üstte **siyah bant** oluşur; altyazı
 * görüntünün ortasına denk gelir ve kullanıcı istediği gibi bandın içine
 * giremez. Kontrol çubuğuyla da çakışabiliyordu.
 *
 * ## Yeni model
 *
 * Konum, **resmin alt kenarına** göre yüzde cinsinden bir kaydırmadır:
 *
 * ```text
 *        +---------------------------+
 *        |     siyah bant (üst)      |
 *        |  +---------------------+  |
 *   +% ->|  |                     |  |   yukarı: görüntünün içine
 *        |  |       GÖRÜNTÜ        |  |
 *        |  |                     |  | <- resmin alt kenarı = 0
 *        |  +---------------------+  |
 *  -% -> |     siyah bant (alt)      |
 *        +---------------------------+
 * ```
 *
 * `0` → altyazı tam resmin alt kenarında (bant varsa bandın üst sınırında).
 * Negatif → bandın içine iner. Pozitif → görüntünün içine yükselir.
 *
 * ## Neden ölçüm gerekli?
 *
 * Resmin alt kenarının kutu tabanına uzaklığı, yalnızca videonun **gerçek**
 * en-boy oranı biliniyorsa hesaplanabilir (`object-fit: contain` bu oranı
 * kutuya göre küçülterip ortalar). 2.35:1 bir dosyada bu uzaklık kutunun
 * ~%15'idir; ölçülmezse "siyah banda denk gel" ayarı bile yanlış yere oturur.
 */

/** Kullanıcı kaydırmasının aralığı: yüzde, kutu yüksekliğine göre. */
export const POSITION_MIN_PERCENT = -20;
export const POSITION_MAX_PERCENT = 40;

/**
 * Varsayılan kaydırma.
 *
 * Küçük bir pozitif değer: hem 16:9 (bant yok) hem 2.35:1 (bant var) içinde
 * makul durur ve kadrajı kapatmaz. Eski sabit `12%` değeri 2.35:1 filmlerde
 * görüntünün çok üstüne denk geliyordu.
 */
export const DEFAULT_POSITION_PERCENT = 4;

export interface PictureBounds {
  /** Kutu tabanından **resmin alt kenarına** uzaklık (px) = alttaki siyah bant. */
  bottomPx: number | null;
  /** Kutu tabanından **resmin üst kenarına** uzaklık (px). */
  topPx: number | null;
  /** Resmin kutu içinde kapladığı yükseklik (px). */
  heightPx: number | null;
}

export const EMPTY_PICTURE_BOUNDS: PictureBounds = {
  bottomPx: null,
  topPx: null,
  heightPx: null,
};

export interface SubtitlePositionInput extends PictureBounds {
  /** Kutu (`.watch__stage`) yüksekliği. */
  stageHeightPx: number | null;
  /** Kullanıcı kaydırması (yüzde; negatif = siyah banta doğru). */
  positionPercent: number;
  /**
   * Kontrol çubuğu yüksekliği + güvenlik payı (px).
   *
   * Altyazı **asla** kontrol çubuğunun altına giremez: kaydırma aşağı
   * çekilse bile bu zemin onu yukarı iter. Aksi hâlde "siyah banda indir"
   * denince kullanıcı oynatmayı değil, kontrol düğmelerini kaybederdi.
   */
  controlsSafePx: number | null;
}

/** Kaydırmayı izin verilen aralığa kıstırır (bozuk `localStorage` koruması). */
export function clampPositionPercent(value: number): number {
  if (!Number.isFinite(value)) return DEFAULT_POSITION_PERCENT;
  return Math.min(Math.max(value, POSITION_MIN_PERCENT), POSITION_MAX_PERCENT);
}

/**
 * Altyazının kutu tabanına uzaklığı (px) — yani CSS `bottom` değeri.
 *
 * Sıralama bilinçlidir:
 *
 *   1. taban  = resmin alt kenarı  (`bottomPx`)
 *   2. kaydır = konum% × kutu yüksekliği
 *   3. taban → kaydır
 *   4. kontrol çubuğu zemini ile **aşağı sınırla**
 *   5. resmin üst kenarıyla **yukarı sınırla** (görüntünün dışına çıkmasın)
 *
 * @returns `null` → ölçüm yok; CSS yedeği devreye girer.
 */
export function computeSubtitleBottomPx(input: SubtitlePositionInput): number | null {
  const { stageHeightPx, bottomPx, topPx, positionPercent, controlsSafePx } = input;
  if (stageHeightPx === null || stageHeightPx <= 0) return null;

  // Resmin alt kenarı bilinmiyorsa kutuyu taban kabul et: ölçüm yapılamadığı
  // anlarda eski davranışa en yakın konuma düşer.
  const pictureBottom = Math.max(0, bottomPx ?? 0);
  const shifted = pictureBottom + (clampPositionPercent(positionPercent) / 100) * stageHeightPx;

  const floor = controlsSafePx ?? 0;
  const ceiling = topPx === null ? stageHeightPx : Math.max(topPx, floor);

  return Math.round(Math.min(Math.max(shifted, floor), ceiling));
}

/**
 * "Siyah banta indir" için hedef kaydırma.
 *
 * Bant yoksa (16:9) **null** döner: kullanıcıya işe yaramaz bir ayar
 * değiştirmiş gibi bir düğme göstermek yanıltıcı olur.
 */
export function letterboxPositionPercent(bounds: PictureBounds): number | null {
  const { bottomPx, heightPx } = bounds;
  if (bottomPx === null || heightPx === null) return null;
  // Bant resmin yüksekliğinden küçükse "siyah bant" saymaz (yuvarlama hatası).
  if (bottomPx < 4 || heightPx <= 0) return null;
  const half = (bottomPx / 2 / heightPx) * 100;
  return clampPositionPercent(-Math.min(half, Math.abs(POSITION_MIN_PERCENT)));
}
